import snakeCase from 'lodash/snakeCase';
import { useEffect, useRef } from 'react';
import { useAppSelector } from 'hooks/redux';

import { authSelectors } from 'app-components/flux/auth';
import { subscriptionsSelectors, type SubscriptionTrackingProps } from 'app-components/flux/subscriptions';
import { getLogger, tracker } from 'app-components/utils/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import mixpanel, { loadSessionRecorder } from 'utils/core/mixpanel';
import { getMixpanelDefaultProperties, getMixpanelUserData } from 'data/mixpanel/mixpanelSelectors';

const log = getLogger('utils/mixpanel.js');

type SubscriptionTrackingValue = SubscriptionTrackingProps[string];

export const useMixpanelSessionReplay = (enabled: boolean) => {
  const wantedRef = useRef(false);
  useEffect(() => {
    wantedRef.current = enabled;
    if (enabled) {
      // The recorder code loads on demand. Load it before starting, and start only if replay is still wanted: once
      // it's loaded, start and stop behave as they did with the recorder bundled, so a stop can't be overtaken by a
      // start that was still waiting for the code.
      loadSessionRecorder()
        .then(() => {
          if (wantedRef.current) {
            mixpanel?.start_session_recording?.();
          }
        })
        .catch((error) => log.error('Mixpanel session recorder failed to load', error));
    } else {
      mixpanel?.stop_session_recording?.();
    }
  }, [enabled]);
};

const Mixpanel = () => {
  const authSession = useAppSelector(authSelectors.getAuthSession);
  const mixpanelUserData = useAppSelector(getMixpanelUserData);
  const mixpanelDefaultProps = useAppSelector(getMixpanelDefaultProperties);
  const subscriptionTrackingProps = useAppSelector((state) =>
    subscriptionsSelectors.getSubscriptionTrackingProps(state, authSession?.datasetId),
  );

  useEffect(() => {
    const subscriptionProps: Record<string, SubscriptionTrackingValue> = {};
    Object.keys(subscriptionTrackingProps).forEach((propName) => {
      subscriptionProps[snakeCase(propName)] = subscriptionTrackingProps[propName];
    });
    log.debug('\nmixpanel identify: \n', {
      userId: authSession.qcsId,
      mixpanelDefaultProps,
      subscriptionProps,
      mixpanelUserData,
    });
    tracker.setDefaultProperties({ ...mixpanelDefaultProps, ...subscriptionProps });
    if (authSession.qcsId) {
      tracker.identify(authSession.qcsId);
      tracker.updateProfile(mixpanelUserData);
    }
  }, [authSession.qcsId, mixpanelUserData, mixpanelDefaultProps, subscriptionTrackingProps]);

  const mixpanelSessionReplay = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'mixpanelSessionReplay'),
  );
  useMixpanelSessionReplay(Boolean(mixpanelSessionReplay));

  return null;
};

export default Mixpanel;
